import Sidebar from "./Sidebar";
import CommandPalette from "./CommandPalette";
import { FeedbackButton } from "./FeedbackButton";
import NotificationBell from "./NotificationBell";
import MobileTabBar from "./mobile/MobileTabBar";
import { OnboardingGate } from "./OnboardingGate";
import ProductTour from "./ProductTour";
import { useAuth } from "../lib/auth";
import { useKeyboard } from "../lib/keyboard";
import { Search, Command, Menu, ChevronLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";

const NAV_PATHS = ["/dashboard", "/clients", "/inbox", "/cases", "/contracts", "/legal", "/schedule", "/commissions", "/position-map"];

// Routes where a back button would be useless — they're top-level destinations.
const ROOT_PATHS = new Set([
  "/dashboard", "/clients", "/inbox", "/cases", "/contracts", "/legal",
  "/mandates", "/schedule", "/notes", "/commissions", "/travel-planner",
  "/agenda", "/assistant", "/briefing", "/contacts", "/today", "/programs", "/matches", "/clauses", "/requests",
  "/reports", "/compliance", "/position-map", "/audit-log",
  "/settings/integrations", "/settings/team", "/settings/permissions",
  "/settings/profile",
]);

import { InstallPrompt } from "../pwa/InstallPrompt";
import { translate } from "../lib/i18n";

export default function Layout({ children, title, subtitle, right, compact = false, mobileFlush = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const showTabBar = user?.role === "agent" || user?.role === "admin";
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Show the back button on any non-root page (e.g. /clients/<id>,
  // /cases/<id>, /reports/<id>). Roots have no upstream to return to.
  const canGoBack = !ROOT_PATHS.has(location.pathname) && window.history.length > 1;
  useKeyboard({
    onPalette: () => setPaletteOpen(true),
    onNumber: (n) => { const p = NAV_PATHS[n - 1]; if (p) navigate(p); },
  });
  return (
    <div className="flex pm-app-root pm-grain">
      <OnboardingGate user={user} />
      {showTabBar && <ProductTour />}
      <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      <main className="flex-1 min-w-0">
        <header
          className={`sticky top-0 z-30 px-4 lg:px-10 py-2.5 ${compact ? "lg:py-2.5" : "lg:py-5"} min-h-14 flex items-center gap-3 bg-white/90 lg:bg-white/70 backdrop-blur-xl border-b`}
          style={{ borderColor: "var(--pm-border)" }}
        >
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="lg:hidden w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 active:bg-slate-200 shrink-0"
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation"
          >
            <Menu size={18} />
          </button>
          {canGoBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full lg:rounded-xl bg-slate-100 lg:bg-white lg:border border-slate-200 flex items-center justify-center text-slate-700 hover:border-slate-300 shrink-0 transition-colors"
              data-testid="header-back-btn"
              aria-label="Go back"
              title="Go back"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="hidden lg:block text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
              {subtitle || "Playmaker"}
            </div>
            <h1 className={`font-display font-bold lg:font-black text-lg sm:text-2xl ${compact ? "lg:text-xl" : "lg:text-3xl"} tracking-tight text-slate-900 truncate`}>
              {title}
            </h1>
          </div>
          <button
            onClick={() => setPaletteOpen(true)}
            data-testid="global-search-button"
            className="hidden md:flex items-center gap-2 px-3 h-10 rounded-xl bg-white border border-slate-200 w-72 text-sm text-slate-500 hover:border-slate-300 transition-colors"
          >
            <Search size={14} className="text-slate-400" />
            <span>Search everything…</span>
            <kbd className="ml-auto text-[10px] font-mono-tab text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded flex items-center gap-1">
              <Command size={9} />K
            </kbd>
          </button>
          <NotificationBell />
          {right}
        </header>
        <section className={`${mobileFlush ? "px-0" : "px-4"} lg:px-10 ${compact ? "py-0 lg:py-3" : "py-5 lg:py-8"} ${showTabBar ? "pb-[calc(var(--pm-nav-h)+var(--sab)+16px)]" : ""} lg:pb-8 pm-fade`}>
          {["/dashboard", "/today"].includes(location.pathname) && <InstallPrompt userId={user?.id} className={`mb-5 ${mobileFlush ? "mx-4" : ""}`} />}
          {children}
          <footer className="mt-10 pt-4 border-t border-slate-100 text-[11px] text-slate-400 flex flex-wrap gap-3" data-testid="legal-footer"><a href="/privacy" className="hover:text-slate-700">Privacy</a><a href="/terms" className="hover:text-slate-700">Voorwaarden</a><a href="/cookies" className="hover:text-slate-700">Cookies</a><span>· {translate("Clause Radar-bedragen zijn indicatief; de agent bevestigt definitieve bedragen.")}</span></footer>
        </section>
      </main>
      <FeedbackButton />
      {showTabBar && <MobileTabBar />}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
